import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/authContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Verifying your Google sign-in…');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sid = match ? decodeURIComponent(match[1]) : null;
    if (!sid) {
      toast.error('Missing session — please try signing in again.');
      navigate('/admin/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post('/auth/session', null, {
          headers: { 'X-Session-ID': sid },
        });
        // Set the user in context directly from the exchange response
        // (skip /auth/me here to avoid a race with the just-set cookie).
        setUser({ email: data.email, name: data.name, picture: data.picture || '' });
        // Clean the URL so /admin route doesn't re-trigger the callback logic.
        window.history.replaceState({}, '', '/admin');
        toast.success(`Welcome, ${data.name || data.email}`);
        navigate('/admin', { replace: true });
      } catch (err) {
        const code = err?.response?.status;
        if (code === 403) {
          setStatus('This Google account is not authorized for the AAWTA admin.');
          toast.error('Access denied — this Google account is not authorized.');
        } else {
          setStatus('Sign-in failed. Please try again.');
          toast.error('Sign-in failed.');
        }
        setTimeout(() => navigate('/admin/login', { replace: true }), 2200);
      }
    })();
  }, [location.hash, navigate, setUser]);

  return (
    <div className="min-h-screen bg-aawta-navy text-white flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="mx-auto w-12 h-12 border-2 border-white/25 border-t-aawta-magenta rounded-full animate-spin" />
        <div className="mt-5 font-display text-xl">{status}</div>
        <div className="mt-2 text-white/60 text-sm">You’ll be redirected shortly.</div>
      </div>
    </div>
  );
};

export default AuthCallback;
